/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { Navigate } from 'react-router-dom'

import Axios, { Canceler, CancelToken } from 'axios'
import { isSameDay, isValid as isValidDate, parse } from 'date-fns'
import { Iterable, Map } from 'immutable'
import * as _ from 'lodash-es'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'
import { change, formValueSelector, getFormMeta, getFormSyncErrors, isValid, reduxForm } from 'redux-form/immutable'

import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'
import { ImmutableTypedMap } from '@lyrahealth-inc/shared-app-logic/src/features/common/types/Immutable'
import { getDisplayDate } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { isLeidosUser } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { isValidDateOfBirth } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/validationUtils'
import { getAllAvailableCodesSet } from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { LTSessionTypes } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import { LyraCodeValidationInfo } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { ICD10_PRIMARY_FIELD, ICD10_SECONDARY_FIELD } from '@lyrahealth-inc/shared-app-logic/src/models/icd10/constants'
import { AvailableCodes } from '@lyrahealth-inc/shared-app-logic/src/models/icd10/ICD10'
import {
  Address,
  ChargeEligiblityPayload,
  ChargeHealthPlanEligibilityPayload,
  ChargeHealthPlanEligiblityResponse,
  ProviderAdminProviderInfo,
  ProviderRates,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { Transfer } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'
import { ProviderUser } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import ContentLayout from '@lyrahealth-inc/ui-core/lib/molecules/layouts/base/ContentLayout'
import BootstrapContainer from '@lyrahealth-inc/ui-core/lib/molecules/layouts/bootstrapContainer/BootstrapContainer'
import { BaseModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/baseModal/BaseModal'

import {
  getHealthPlanCurrentCharge,
  getHealthPlanHPEligibility,
  getHealthPlanICD10s,
} from './../data/healthPlan/healthPlanSelectors'
import CancelledSessionsExceededModal from './CancelledSessionsExceededModal'
import ClientIneligibleModal from './ClientIneligibleModal'
import * as requestPaymentActions from './data/requestPaymentActions'
import DuplicatePaymentModal from './DuplicatePaymentModal'
import FirstSessionModal from './FirstSessionModal'
import LastSessionModal from './LastSessionModal'
import OutcomeReminder from './OutcomeReminder'
import styles from './requestPayment.module.scss'
import SessionsExceededModal from './SessionsExceededModal'
import SessionsWarningModal from './SessionsWarningModal'
import * as clientsDataActions from '../clients/data/clientsDataActions'
import AvailabilityCard from '../common/components/availabilityCard/AvailabilityCard'
import paymentRequestAsyncValidate from '../common/components/forms/paymentRequest/paymentRequestAsyncValidate'
import PaymentRequestForm from '../common/components/forms/paymentRequest/PaymentRequestForm'
import paymentRequestValidate from '../common/components/forms/paymentRequest/paymentRequestValidate'
import {
  bannerMessages,
  coachingSessionTypesSet,
  LT_COACH_ROLES,
  LYRA_CODE_LENGTH,
  outcomesEligibleSessionTypes,
  PAYMENT_FORM,
  PAYMENT_TYPES,
  ROLES,
  specialityCareAttestationStatuses,
} from '../common/constants/appConstants'
import { SET_HP_ELIGIBILITY_STATUS } from '../common/constants/reduxConstants'
import {
  CLIENT_EMAIL,
  HEALTHPLAN_INFO,
  PRACTICES_DETAILS_HEALTHPLAN_PAYMENT_INFO,
  PRACTICES_DETAILS_NEW_PAYMENT,
  PRACTICES_REGISTER,
  PROVIDERS_DETAILS_HEALTHPLAN_PAYMENT_INFO,
  PROVIDERS_DETAILS_NEW_PAYMENT,
  PROVIDERS_REGISTER,
} from '../common/constants/routingConstants'
import { RoleConfig } from '../common/roleConfigs'
import { scrollToPageTop } from '../common/utils/scrollToPageTop'
import {
  convertToFormData,
  getChargeCopyPrepopInfo,
  getErrorText,
  getPatientPrepopInfo,
  hasRole,
  sanitizeLyraCode,
  shouldShowPaymentFormEligibleMemberDetails,
} from '../common/utils/utils'
import * as alertActions from '../data/alertActions'
import { getAuthConfig, getAuthUser } from '../data/auth/authSelectors'
import {
  clearHealthPlanCharge,
  clearHPEligibility,
  getHPEligibility,
  saveHealthPlanCharge,
} from '../data/healthPlanAutoActions'
import {
  getRequestPaymentCopy,
  getRequestPaymentCustomers,
  getRequestPaymentDataData,
  getRequestPaymentDataOutcomeReminderStatus,
  getRequestPaymentDataSessionTrackingStatus,
  getRequestPaymentDiffPricingEnabled,
  getRequestPaymentDiffPricingModifier,
  getRequestPaymentEligibilityStatus,
  getRequestPaymentFormSponsoringCompanies,
  getRequestPaymentLyraCodeValidationInfo,
  getRequestPaymentPatient,
  getRequestPaymentProvidersList,
  getRequestPaymentRates,
  getRequestPaymentResponseDataSessionTrackingStatus,
  getRequestPaymentSelectedProvider,
  getRequestPaymentShowCancelledSessionsExceededModal,
  getRequestPaymentShowDuplicateModal,
  getRequestPaymentShowIneligibleClientModal,
} from '../data/requestPayment/requestPaymentSelectors'
import { RootState } from '../data/store'
import { useLeidosFieldReset, useLeidosLyraCodeDOB } from '../hooks/useLeidosFormEffects'
import { getPayments } from '../payments/dashboard/data/paymentsDashboardActions'
import { getPracticeDetailsDataId } from '../practices/individualPractice/data/practiceDetailsSelectors'
import {
  getProviderDetailsAddresses,
  getProviderDetailsData,
  getProviderDetailsDiffPricingEnabled,
  getProviderDetailsDiffPricingModifier,
} from '../providers/data/providerSelectors'
import * as providerDetailsActions from '../providers/individualProvider/data/providerDetailsActions'

export type RequestPaymentProps = {
  handleSubmit: () => void
  initialize: (initialValues: object) => void
  submitting: boolean
  user: ProviderUser
  providersList: ProviderAdminProviderInfo[]
  showEligibleMemberDetails: boolean
  sessionType: string
  modality: string
  showDiagnosisDescription: boolean
  clientOutcomesEligibility: boolean
  enteredPatient: { first_name: string; last_name: string; dob: string }
  initialCompany: CustomerInfo[]
  initialDiagnoses: { label: string; value: string }[]
  initialICD10s: { icd10_primary_diagnosis: string; icd10_secondary_diagnosis: string }
  availableCodes: AvailableCodes
  dispatch: Dispatch<AnyAction>
  customers: CustomerInfo[]
  displayCustomers: CustomerInfo[]
  sessionTrackingStatus: string
  sessionsExceeded: string
  paymentResponse: Transfer
  showDuplicateModal: boolean
  showCancelledSessionsExceededModal: boolean
  enteredVisitDate: string
  guaranteedTimeEligible: boolean
  guaranteedTime: boolean
  providerOutcomesAgreed: boolean
  patientOutcomesAgreed: boolean
  rates: ProviderRates
  outcomeReminderStatus: { show_reminder: boolean; outcome_sent_date: string }
  selectedCompany: CustomerInfo[]
  config: RoleConfig
  prepopInfo: any
  formIsValid: boolean
  practiceID: string
  activeField: string
  asyncValidate: (fieldName: string, fieldValue: string, manual: string) => void
  enteredlyraCode: string
  touch: (fieldName: string) => void
  eligibilityCheckFields: ChargeEligiblityPayload
  HPEligibilityCheckFields: ChargeHealthPlanEligibilityPayload
  HPEligibility?: ChargeHealthPlanEligiblityResponse
  currentHPCharge?: any
  synchronousErrors: any
  showIneligibleClientModal: boolean
  eligibilityStatus: boolean
  lyraCodeValidationInfo: LyraCodeValidationInfo
  selectedProvider: ProviderAdminProviderInfo
  isPractice: boolean
  attendance: string
  providerAddresses: Address[]
  primaryCondition: { label: string; value: string }[]
  secondaryCondition: { label: string; value: string }[]
  ebtType: { label: string; value: string }[]
  differentialPricingEnabled: boolean
  differentialPricingModifier: number
  actions: {
    getPracticeProviders: (id: string) => Promise<any>
    addAlert: ({
      show,
      contents,
      style,
      expires,
      autoDismissTimer,
    }: {
      show: boolean
      contents: string
      style: string
      expires: boolean
      autoDismissTimer?: number
    }) => void
    getProviderRates: ({ id }: { id: string }) => void
    getProvider: (providerId: string) => void
    getChargeToCopy: (chargeId: string) => Promise<any>
    selectProvider: (providerId: string) => void
    clearHPEligibility: () => void
    clearHealthPlanCharge: () => void
    checkEligibility: (eligiblityObj: FormData) => Promise<any>
    displayIneligibleClientModal: () => void
    getHPEligibility: (hpEligibilityObj: ChargeHealthPlanEligibilityPayload) => Promise<any>
    getPayments: ({
      status,
      page,
      limit,
      providerId,
      isHealthPlan,
      patientId,
    }: {
      status: string
      page: number
      limit: number
      providerId: string
      isHealthPlan: boolean
      patientId: string
    }) => Promise<any>
    getClient: (
      enteredPatient: { first_name: string; last_name: string; dob: string },
      cancelFunction: CancelToken,
      providerId?: string,
    ) => Promise<any>
    hideAlerts: () => void
    submitPaymentRequest: (data: FormData, isBCx: boolean) => Promise<any>
    saveHealthPlanCharge: (HPCharge: any) => void
    clearLyraCodeValidationInfo: () => void
    closeRequestPaymentModal: (modalType: string) => void
    clearRequestPaymentStore: () => void
  }
}

const cancelToken = Axios.CancelToken
let cancelGetClient: Canceler

const requestPaymentWarnings = ($$values = Map(), props: RequestPaymentProps) => {
  const warnings: Record<string, string> = {}
  // Only display lyra code date of birth match warning for payments admin
  if (hasRole(props.user.roles, ROLES.PAYMENTS_ADMIN)) {
    const lyraCodeDOB = props.lyraCodeValidationInfo?.user_info?.dob
    const enteredDOB = $$values.get(PAYMENT_FORM.FIELDS.DATE_OF_BIRTH) as string
    if (lyraCodeDOB && enteredDOB) {
      const parsedEnteredDOB = parse(enteredDOB, 'MM/dd/yyyy', new Date())
      const parsedLyraDOB = parse(lyraCodeDOB, 'MM-dd-yyyy', new Date())
      if (
        !isValidDate(parsedEnteredDOB) ||
        !isValidDate(parsedLyraDOB) ||
        !isSameDay(parsedEnteredDOB, parsedLyraDOB)
      ) {
        warnings[PAYMENT_FORM.FIELDS.DATE_OF_BIRTH] = 'Date of birth does not correspond with Lyra code'
      }
    }
  }
  return warnings
}

export const RequestPaymentAdmins: React.FC<RequestPaymentProps> = ({
  user,
  providersList,
  practiceID,
  initialize,
  guaranteedTime,
  currentHPCharge,
  sessionType,
  modality,
  rates,
  prepopInfo,
  synchronousErrors,
  dispatch,
  eligibilityCheckFields,
  HPEligibilityCheckFields,
  sessionTrackingStatus,
  HPEligibility,
  initialICD10s,
  showCancelledSessionsExceededModal,
  showDuplicateModal,
  paymentResponse,
  sessionsExceeded,
  outcomeReminderStatus,
  customers,
  displayCustomers,
  enteredVisitDate,
  enteredPatient,
  activeField,
  enteredlyraCode,
  touch,
  asyncValidate,
  selectedProvider,
  attendance,
  selectedCompany,
  eligibilityStatus,
  formIsValid,
  availableCodes,
  handleSubmit,
  submitting,
  showEligibleMemberDetails,
  showDiagnosisDescription,
  showIneligibleClientModal,
  clientOutcomesEligibility,
  initialCompany,
  initialDiagnoses,
  guaranteedTimeEligible,
  providerOutcomesAgreed,
  patientOutcomesAgreed = true,
  config,
  isPractice,
  providerAddresses,
  lyraCodeValidationInfo,
  differentialPricingEnabled,
  differentialPricingModifier,
  actions: {
    getPracticeProviders,
    addAlert,
    getProviderRates,
    getProvider,
    getChargeToCopy,
    selectProvider,
    clearHPEligibility,
    clearHealthPlanCharge,
    checkEligibility,
    displayIneligibleClientModal,
    getHPEligibility,
    getPayments,
    getClient,
    hideAlerts,
    submitPaymentRequest,
    saveHealthPlanCharge,
    clearLyraCodeValidationInfo,
    closeRequestPaymentModal,
    clearRequestPaymentStore,
  },
  primaryCondition,
  secondaryCondition,
  ebtType,
}) => {
  const [loading, setLoading] = useState(true)
  const [isHPEligibilityLoading, setIsHPEligibilityLoading] = useState(false)
  const [pastPaymentLoading, setPastPaymentLoading] = useState(false)
  const [submittedEligibityCheckData, setSubmittedEligibilityCheckData] = useState({})
  const [submittedHPEligibilityCheckData, setSubmittedHPEligibilityCheckData] = useState({})
  const [dateInputKey, setDateInputKey] = useState(_.uniqueId('dateInput'))
  const prevActiveField = usePrevious(activeField)
  const prevLyraCode = usePrevious(enteredlyraCode)
  const prevGuaranteedTime = usePrevious(guaranteedTime)
  const prevPrepopInfo = usePrevious(prepopInfo)
  const prevSessionType = usePrevious(sessionType)
  const prevRates = usePrevious(rates)
  const prevSelectedCompany = usePrevious(selectedCompany)
  const customerName = selectedCompany?.[0]?.value
  const prevModality = usePrevious(modality)
  const location = useLocation()
  const navigate = useNavigate()

  const roles = user.roles

  // https://stackoverflow.com/questions/54666401/how-to-use-throttle-or-debounce-with-react-hook
  const useDebouncedEffect = (effect: () => void, deps: Array<string>, delay: number) => {
    useEffect(() => {
      const handler = setTimeout(() => effect(), delay)

      return () => clearTimeout(handler)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay])
  }

  // This function is specific to admins roles
  // In RequestPayment, there is a useEffect hook similar to this but for non-admin roles which has a similar comment
  // REMINDER: When we merge these two files, we will need both useEffect hooks.
  useEffect(() => {
    // practice_admins: get providers if you don't already have them
    if (hasRole(roles, [ROLES.PRACTICES_ADMIN, ROLES.PAYMENTS_ADMIN]) && providersList.length == 0 && practiceID) {
      getPracticeProviders(practiceID).then(
        () => {
          // got providers
          setLoading(false)
          const providerIdToLoad = currentHPCharge?.provider_lyra_id
          if (providerIdToLoad) {
            selectProvider(providerIdToLoad)
            getProviderRates({ id: providerIdToLoad })
          }
        },
        (error: any) => {
          setLoading(false)
          const errorText = error?.response?.data?.message || getErrorText(error?.response?.status) || 'Network error'
          addAlert({
            show: true,
            contents: 'Error in getting list of providers: ' + errorText,
            style: 'danger',
            expires: true,
          })
        },
      )
    } else {
      setLoading(false)
    }
    return function cleanup() {
      clearRequestPaymentStore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const userId = user.id
    if (hasRole(roles, ROLES.INDIVIDUAL_PROVIDER)) {
      getProviderRates({ id: userId })
      getProvider(userId)
    }
  }, [user, roles, getProviderRates, getProvider])

  useEffect(() => {
    const prevRoute = location.state?.prev_route
    if (
      prevRoute !== PRACTICES_DETAILS_HEALTHPLAN_PAYMENT_INFO.route &&
      prevRoute !== PROVIDERS_DETAILS_HEALTHPLAN_PAYMENT_INFO.route &&
      prevRoute !== HEALTHPLAN_INFO.route
    ) {
      clearHPEligibility()
      clearHealthPlanCharge()
    }
  }, [clearHPEligibility, clearHealthPlanCharge, location])

  useEffect(() => {
    const copiedChargeID = location.state?.copy_charge_id

    if (copiedChargeID) {
      getChargeToCopy(copiedChargeID).then(
        (rpReturn: any) => {
          if (hasRole(roles, ROLES.PRACTICES_ADMIN) && rpReturn.value.data.provider_lyra_id) {
            getProviderRates({ id: rpReturn.value.data.provider_lyra_id })
          }
          // Call getInitialValues inside getChargeToCopy callback to make sure this.props.prepopInfo is populated]
          if (Object.keys(prepopInfo).length > 2) {
            const onlyUseCopiedCharge = true
            const { initialValues } = getInitialValues(guaranteedTime, onlyUseCopiedCharge)
            initialize(initialValues)
          }
        },
        (error: any) => {
          const errorText = error?.response?.data?.message || getErrorText(error?.response?.status) || 'Network error'
          addAlert({
            show: true,
            contents: errorText,
            style: 'danger',
            expires: true,
          })
        },
      )
    } else {
      const { initialValues } = getInitialValues(guaranteedTime)
      initialize(initialValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (prepopInfo?.provider_lyra_id && !(providersList?.length == 0) && !selectedProvider) {
      selectProvider(prepopInfo.provider_lyra_id)
    }
  })

  useEffect(() => {
    // When company, lyra code, or visit date lose focus trigger eligibilty check
    const eligibilityTriggerFields = [
      PAYMENT_FORM.FIELDS.COMPANY,
      PAYMENT_FORM.FIELDS.LYRA_CODE,
      PAYMENT_FORM.FIELDS.VISIT_DATE,
    ]
    if (
      eligibilityTriggerFields.some((field) => {
        return field === prevActiveField && field !== activeField
      })
    ) {
      eligibilityCheck()
    }

    if (
      Object.keys(HPEligibilityCheckFields).some((field) => {
        return field === prevActiveField && field !== activeField && field !== PAYMENT_FORM.FIELDS.PROVIDER_LYRA_ID
      })
    ) {
      checkHPEligibility()
    }
  })

  const _getClient = () => {
    if (cancelGetClient) {
      cancelGetClient() // cancel any pending axios requests
    }
    getClient(
      enteredPatient,
      new cancelToken(function executor(c) {
        // An executor function receives a cancel function as a parameter
        cancelGetClient = c
      }),
      prepopInfo.provider_lyra_id,
    )
  }

  useDebouncedEffect(
    () => {
      //new values entered

      // test them
      if (
        enteredPatient.first_name &&
        enteredPatient.first_name.length > 1 &&
        enteredPatient.last_name &&
        enteredPatient.last_name.length > 1 &&
        enteredPatient.dob &&
        isValidDate(parse(enteredPatient.dob, 'MM/dd/yyyy', new Date())) &&
        (!hasRole(roles, ROLES.PAYMENTS_ADMIN) || prepopInfo.provider_lyra_id)
      ) {
        // valid required data for retrieving patient
        _getClient()
      }
    },
    [enteredPatient.first_name, enteredPatient.last_name, enteredPatient.dob, prepopInfo.provider_lyra_id],
    1000,
  )

  useEffect(() => {
    // checks for updated company field and force sessionType to be cleared(required to be reselected again)
    // coach's session type should never be touched as they can only submit smp_session + field is disabled
    const prevCompany = prevSelectedCompany?.[0]?.value
    const currCompany = selectedCompany?.[0]?.value
    // We check to see if prevCompany is undefined because if you reload the page after copying a charge in payment history tab, prevCompany will be undefined
    // causing session type to be cleared when it shouldn't be.
    if (prevCompany) {
      if (prevCompany !== currCompany && !hasRole(roles, LT_COACH_ROLES) && !guaranteedTime) {
        dispatch(change(PAYMENT_FORM.NAME, PAYMENT_FORM.FIELDS.SESSION_TYPE, ''))
      }
    }
  }, [dispatch, guaranteedTime, prevSelectedCompany, roles, selectedCompany])

  useLeidosFieldReset(dispatch, selectedCompany, prevSelectedCompany, !!location.state?.copy_charge_id)
  useLeidosLyraCodeDOB(dispatch, selectedCompany, lyraCodeValidationInfo)

  useEffect(() => {
    // Manually trigger async validation. This is needed so that it fires on the last character press
    if (prevLyraCode !== enteredlyraCode && sanitizeLyraCode(enteredlyraCode).length === LYRA_CODE_LENGTH) {
      asyncValidate(PAYMENT_FORM.FIELDS.LYRA_CODE, enteredlyraCode, 'manual')
      // Set lyra_code field to touched so that the error message displays immediately
      touch(PAYMENT_FORM.FIELDS.LYRA_CODE)
    }
  }, [prevLyraCode, enteredlyraCode, asyncValidate, touch])

  const locationChangeHandler = useCallback(
    (address: Address[]) => {
      dispatch(change(PAYMENT_FORM.NAME, PAYMENT_FORM.FIELDS.LOCATION, address))
    },
    [dispatch],
  )

  const getInitialValues = useCallback(
    (isGuaranteedTime: boolean, onlyUseCopiedCharge = false, afterPaymentSubmit = false) => {
      let initialValuesByLyraType = {}
      let disabledFieldsByLyraType = {}
      if (hasRole(roles, [ROLES.LT_THERAPIST, ROLES.INDIVIDUAL_PROVIDER])) {
        initialValuesByLyraType = PAYMENT_FORM.THERAPIST.initialValues
        disabledFieldsByLyraType = PAYMENT_FORM.THERAPIST.disabledFields
      }

      // It is possible that currentHPCharge is in the redux store when a user wants to copy charge from payment history
      // onlyUseCopiedCharge is set to true to make sure the form is filled correctly with the copied charge and not the one in redux store
      const chargeObj = !onlyUseCopiedCharge ? currentHPCharge : {}

      // When copying a payment from payment history and submitting a non-HP charge, prepopInfo will still contain copied charge from redux store when getInitialValues is called
      // SubmitPaymentRequest action clears copied charge from store, however it seems like it waits until after getInitialValues is called, therefore we need to clear out those values in prepopInfo
      let prepopInfoForInit = prepopInfo
      if (afterPaymentSubmit) {
        const { practice_id, provider_lyra_id } = prepopInfo
        prepopInfoForInit = { practice_id, provider_lyra_id }
      }

      // loads the base default values (applicable for all provider types) and then fields based on provider type
      let initialValues = {
        ...PAYMENT_FORM.DEFAULT.initialValues,
        ...initialValuesByLyraType,
        ...prepopInfoForInit,
        ...chargeObj,
      }

      let disabledFields: Record<string, boolean> = { ...disabledFieldsByLyraType }

      // load guaranteed time specific field values and provider+guaranteed time specific fields values if guaranted time box is checked
      if (isGuaranteedTime) {
        let initialValuesByLyraTypeGuaranteedTime = {}
        let disabledFieldsByLyraTypeGuaranteedTime = {}
        if (hasRole(roles, [ROLES.LT_THERAPIST, ROLES.INDIVIDUAL_PROVIDER])) {
          initialValuesByLyraTypeGuaranteedTime = PAYMENT_FORM.THERAPIST_GUARANTEED_TIME.initialValues
          disabledFieldsByLyraTypeGuaranteedTime = PAYMENT_FORM.THERAPIST_GUARANTEED_TIME.disabledFields
        }

        initialValues = {
          ...initialValues,
          ...PAYMENT_FORM.GUARANTEED_TIME.initialValues,
          ...initialValuesByLyraTypeGuaranteedTime,
        }
        disabledFields = {
          ...disabledFields,
          ...PAYMENT_FORM.GUARANTEED_TIME.disabledFields,
          ...disabledFieldsByLyraTypeGuaranteedTime,
        }
      }
      if (modality && sessionType && sessionType !== 'other' && rates && Object.keys(rates).length > 0) {
        if (differentialPricingEnabled && modality == 'in_person') {
          initialValues.session_rate = (
            differentialPricingModifier + parseFloat(rates[sessionType as keyof ProviderRates] as string)
          ).toString()
        } else {
          initialValues.session_rate = rates[sessionType as keyof ProviderRates]
        }
      } else {
        initialValues.session_rate = '0'
      }
      if (initialValues.visit_date) {
        initialValues.visit_date = getDisplayDate(initialValues.visit_date, 'MM/DD/YYYY')
      }

      // disable FN LN until valid DOB is entered
      if (_.get(synchronousErrors, PAYMENT_FORM.FIELDS.DATE_OF_BIRTH)) {
        disabledFields[PAYMENT_FORM.FIELDS.FIRST_NAME] = true
        disabledFields[PAYMENT_FORM.FIELDS.LAST_NAME] = true
      }

      return { initialValues, disabledFields }
    },
    [
      currentHPCharge,
      differentialPricingEnabled,
      differentialPricingModifier,
      modality,
      prepopInfo,
      rates,
      roles,
      sessionType,
      synchronousErrors,
    ],
  )

  useEffect(() => {
    // re-initialize the form if the guaranteedTime box has been toggled. Force fields to update to the calculated initialValues
    const { initialValues } = getInitialValues(guaranteedTime)
    const prepopInfoChanged = _.isEqual(prevPrepopInfo, prepopInfo) === false
    const gtChanged = prevGuaranteedTime !== guaranteedTime
    if (prepopInfoChanged) {
      initialize(initialValues)
    }
    // Skip for Leidos: switching to Leidos dispatches change(IS_GUARANTEED_TIME, false), which triggers this effect.
    // initialize() would re-set the entire form (including COMPANY) and clobber useLeidosFieldReset's controlled reset.
    else if (gtChanged && !isLeidosUser(customerName)) {
      initialize(initialValues)
    }
    // updates the rates when we receive rates from backend, when user changes session_type, or when user changes modality
    if (prevSessionType !== sessionType || prevRates !== rates || modality !== prevModality) {
      dispatch(change(PAYMENT_FORM.NAME, PAYMENT_FORM.FIELDS.SESSION_RATE, initialValues.session_rate))
    }
  }, [
    prevGuaranteedTime,
    guaranteedTime,
    prevPrepopInfo,
    prepopInfo,
    prevSessionType,
    sessionType,
    prevRates,
    rates,
    getInitialValues,
    initialize,
    dispatch,
    modality,
    prevModality,
    customerName,
  ])

  const convertHPEligibilityData = (data: ChargeHealthPlanEligibilityPayload) => {
    const fields = data as Record<string, any>
    Object.keys(fields).forEach((key) => {
      if (key === PAYMENT_FORM.FIELDS.COMPANY) {
        fields[key] = fields[key].map((d: any) => d.value).toString()
      }
      if (
        key === PAYMENT_FORM.FIELDS.DATE_OF_BIRTH ||
        key === PAYMENT_FORM.FIELDS.VISIT_DATE ||
        key === PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_DOB
      ) {
        fields[key] = getDisplayDate(fields[key], 'YYYY-MM-DD')
      }
      if (key === PAYMENT_FORM.FIELDS.RELATIONSHIP) {
        if (fields[key] === 'dependent') {
          // This is done to match the field name and value that PP-API /transfers/healthplan/eligible expects
          fields.patient_relationship = 'dependent_other'
        }
        delete fields[key]
      }
      if (key === PAYMENT_FORM.FIELDS.LYRA_CODE) {
        fields[key] = sanitizeLyraCode(fields[key])
      }
    })
    return data
  }

  const eligibilityCheck = () => {
    // Fire checkEligibility if there are no corresponding errors for eligibilityCheckFields
    // And eligibilityCheck data is different than previous request
    if (
      _.isEmpty(_.pick(synchronousErrors, Object.keys(eligibilityCheckFields))) &&
      !_.isEqual(submittedEligibityCheckData, eligibilityCheckFields)
    ) {
      checkEligibility(convertToFormData(eligibilityCheckFields)).then((response: any) => {
        if (response.user_eligibility.status === false) {
          displayIneligibleClientModal()
        }
        setSubmittedEligibilityCheckData(eligibilityCheckFields)
      })
    }
  }

  // Fires off an eligibility check - POST /v1/transfers/healthplan/eligible
  // Once company, dob, FN/LN, visit_date, and session_type are populated
  const checkHPEligibility = () => {
    const { ATTESTATION_NEEDED } = specialityCareAttestationStatuses
    const convertedHPEligibilityData = convertHPEligibilityData(HPEligibilityCheckFields)
    if (
      _.isEmpty(_.pick(synchronousErrors, Object.keys(HPEligibilityCheckFields))) &&
      !_.isEqual(submittedHPEligibilityCheckData, convertedHPEligibilityData)
    ) {
      setIsHPEligibilityLoading(true) // block form submission if request hasn't finished
      getHPEligibility(convertedHPEligibilityData).then((response: any) => {
        setSubmittedHPEligibilityCheckData(convertedHPEligibilityData)
        setIsHPEligibilityLoading(false)
        dispatch({
          type: SET_HP_ELIGIBILITY_STATUS,
          data: response,
        })
        // If attestation_needed, load the latest transfer for corresponding patient/provider lyra id
        // Transfer data used in HealthPlanInfo to prepopulate attestation fields
        if (response.speciality_care_attestation_status === ATTESTATION_NEEDED && response.patient_lyra_id) {
          setPastPaymentLoading(true)
          getPayments({
            status: 'submitted',
            page: 0,
            limit: 20,
            providerId: convertedHPEligibilityData.provider_lyra_id,
            isHealthPlan: true,
            patientId: response.patient_lyra_id,
          }).then(() => {
            setPastPaymentLoading(false)
          })
        }
      })
    }
  }

  const providerChangeHandler = (_event: ChangeEvent, providerId: string) => {
    getProviderRates({ id: providerId })

    // Clear the dynamic fields since they are based on the selected provider attributes.
    dispatch(change(PAYMENT_FORM.NAME, PAYMENT_FORM.FIELDS.IS_GUARANTEED_TIME, false))

    clearHPEligibility()
    clearHealthPlanCharge()

    selectProvider(providerId)
  }

  const lastSessionHandler = (e: ChangeEvent<HTMLInputElement>, value: boolean) => {
    if (e.target.name === PAYMENT_FORM.FIELDS.LAST_SESSION && value) {
      dispatch(change(PAYMENT_FORM.NAME, PAYMENT_FORM.FIELDS.LAST_SESSION_TERMINATED, false))
    } else if (e.target.name === PAYMENT_FORM.FIELDS.LAST_SESSION_TERMINATED && value) {
      dispatch(change(PAYMENT_FORM.NAME, PAYMENT_FORM.FIELDS.LAST_SESSION, false))
    }
  }

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const submitForm = (values: Map<any, any>) => {
    hideAlerts()

    const isPaymentsAdmin = hasRole(roles, ROLES.PAYMENTS_ADMIN)
    const chargeObj = values.toJS()
    delete chargeObj.payment_type
    const validCodesSet = getAllAvailableCodesSet(availableCodes)
    const { APPROACHING_ATTESTATION, ATTESTATION_PRIOR_AUTHORIZATION, ATTESTATION_NEEDED } =
      specialityCareAttestationStatuses

    // Coaches should not be asked to provide ICD10 and verify co-pay consent for coaching health plan sessions.
    // Default values will be used when the health plan claim is generated.
    // DBT group sessions always require ICD-10 codes regardless of payment type (HPI or self-pay).
    const isDBTGroupSession = chargeObj.session_type === LTSessionTypes.DBT_GROUP_SESSION
    if (
      !coachingSessionTypesSet.has(chargeObj.session_type) &&
      (HPEligibility?.next_session_uses_health_plan ||
        (isDBTGroupSession && HPEligibility) ||
        (HPEligibility &&
          [APPROACHING_ATTESTATION, ATTESTATION_PRIOR_AUTHORIZATION, ATTESTATION_NEEDED].includes(
            HPEligibility?.speciality_care_attestation_status,
          )))
    ) {
      chargeObj.next_session_uses_health_plan = HPEligibility.next_session_uses_health_plan
      chargeObj.speciality_care_attestation_status = HPEligibility.speciality_care_attestation_status
      chargeObj.patient_lyra_id = HPEligibility.patient_lyra_id
      if (HPEligibility.payment_type === PAYMENT_TYPES.SELF_PAY) {
        chargeObj.payment_type = HPEligibility.payment_type
      }
      if (initialICD10s.icd10_primary_diagnosis && validCodesSet.has(initialICD10s.icd10_primary_diagnosis)) {
        chargeObj.icd10_primary_diagnosis = initialICD10s.icd10_primary_diagnosis
      }

      if (initialICD10s.icd10_secondary_diagnosis && validCodesSet.has(initialICD10s.icd10_secondary_diagnosis)) {
        chargeObj.icd10_secondary_diagnosis = initialICD10s.icd10_secondary_diagnosis
      }
      scrollToPageTop()
      saveHealthPlanCharge(chargeObj)
      if (isPaymentsAdmin && location.pathname === PROVIDERS_DETAILS_NEW_PAYMENT.route) {
        navigate(PROVIDERS_DETAILS_HEALTHPLAN_PAYMENT_INFO.route)
      } else if (isPaymentsAdmin && location.pathname === PRACTICES_DETAILS_NEW_PAYMENT.route) {
        navigate(PRACTICES_DETAILS_HEALTHPLAN_PAYMENT_INFO.route)
      } else {
        navigate(HEALTHPLAN_INFO.route)
      }
    } else {
      return submitPaymentRequest(convertToFormData(chargeObj), false)
        .then((rpReturn: any) => {
          scrollToPageTop()
          if (!isPaymentsAdmin && (rpReturn.data.email_required || rpReturn.data.health_plan_reminder_email_required)) {
            navigate(CLIENT_EMAIL.route, {
              state: {
                patient_id: rpReturn.data.data.patient_lyra_id,
                initialSession: rpReturn.data.session_tracking_status === 'session_limit_initial_session',
                sessionsLeft: parseInt(rpReturn.data.data.sessions_remaining, 10),
                firstName: rpReturn.data.data.first_name,
                lastName: rpReturn.data.data.last_name,
                isHealthPlanEligible: rpReturn.data.health_plan_reminder_email_required,
                company: rpReturn.data.data.company,
                customer: customers.find((customer) => customer.value === rpReturn.data.data.company),
              },
            })
          } else {
            // forward them along with success message
            clearLyraCodeValidationInfo()
            const { initialValues } = getInitialValues(guaranteedTime, false, true)
            initialize(initialValues)
            dispatch(change(PAYMENT_FORM.NAME, PAYMENT_FORM.FIELDS.SESSION_RATE, initialValues.session_rate))
            // Hack: Setting a new key to reset the DateInputField after submit.
            // This should go away when we move to react-final-form
            setDateInputKey(_.uniqueId('dateInput'))
            if (
              sessionTrackingStatus === 'session_limit_warning' ||
              sessionTrackingStatus === 'session_limit_initial_session'
            ) {
              return
            }
            addAlert({
              show: true,
              contents: bannerMessages.SUBMIT_PAYMENT_SUCCESS,
              style: 'success',
              expires: true,
              autoDismissTimer: 10000,
            })
          }
        })
        .catch(() => scrollToPageTop())
    }
  }

  const buildModals = () => {
    let showModal = false
    let modalBody = <div />
    let modalType = ''

    // prep payment info if exists
    let sessionsLeft
    let company: CustomerInfo | undefined
    let firstName = enteredPatient.first_name
    let lastName = enteredPatient.last_name
    if (paymentResponse) {
      firstName = paymentResponse.first_name
      lastName = paymentResponse.last_name
      company = customers.find((customer: CustomerInfo) => customer.value === paymentResponse.company)
      if (!_.isEmpty(paymentResponse.sessions_remaining)) {
        sessionsLeft = parseInt(paymentResponse.sessions_remaining, 10)
      }
    }

    // session tracking modal
    switch (sessionTrackingStatus) {
      case 'session_limit_warning':
        showModal = true
        if (sessionsLeft === 0) {
          modalType = 'STS_LAST'
          modalBody = <LastSessionModal firstName={firstName} lastName={lastName} company={company} />
          break
        }
        modalType = 'STS_WARNING'
        modalBody = (
          <SessionsWarningModal
            firstName={firstName}
            lastName={lastName}
            sessionsLeft={sessionsLeft ?? 0}
            company={company}
          />
        )
        break
      case 'session_limit_initial_session':
        showModal = true
        modalType = 'STS_INITIAL'
        modalBody = (
          <FirstSessionModal
            firstName={firstName}
            lastName={lastName}
            sessionsLeft={sessionsLeft ?? 0}
            customer={company}
          />
        )
        break
      // outcome reminder modal
      case 'session_limit_ok':
        if (outcomeReminderStatus?.show_reminder && !isLeidosUser(paymentResponse?.company)) {
          showModal = true
          modalType = 'outcomeReminder'
          modalBody = (
            <OutcomeReminder
              firstName={firstName}
              outcomeSentDate={getDisplayDate(outcomeReminderStatus?.outcome_sent_date, 'MMMM Do')}
              closeFunc={() => closeRequestPaymentModal(modalType)}
            />
          )
        }
    }
    if (sessionsExceeded === 'session_limit_exceeded') {
      showModal = true
      modalBody = <SessionsExceededModal firstName={firstName} lastName={lastName} company={company} />
      modalType = 'sessionsExceeded'
    }

    // duplicate payment modal
    if (showDuplicateModal) {
      showModal = true
      modalType = 'duplicatePayment'
      modalBody = <DuplicatePaymentModal closeFunc={() => closeRequestPaymentModal(modalType)} />
    }

    // exceeded cancelled sessions modal
    if (showCancelledSessionsExceededModal) {
      showModal = true
      modalBody = (
        <CancelledSessionsExceededModal
          firstName={firstName}
          lastName={lastName}
          visitDate={enteredVisitDate}
          company={company}
        />
      )
      modalType = 'cancelledSessionsExceeded'
    }

    // ineligible client modal
    if (showIneligibleClientModal) {
      showModal = true
      modalType = 'ineligibleClient'
      modalBody = <ClientIneligibleModal closeFunc={() => closeRequestPaymentModal(modalType)} />
    }

    return {
      showModal,
      modalBody,
      modalType,
    }
  }

  const submitButtonText = () => {
    const { APPROACHING_ATTESTATION, ATTESTATION_PRIOR_AUTHORIZATION, ATTESTATION_NEEDED } =
      specialityCareAttestationStatuses

    return HPEligibility?.next_session_uses_health_plan ||
      (HPEligibility &&
        [APPROACHING_ATTESTATION, ATTESTATION_PRIOR_AUTHORIZATION, ATTESTATION_NEEDED].includes(
          HPEligibility?.speciality_care_attestation_status,
        ))
      ? 'Next'
      : 'Submit'
  }

  const shouldDisableSubmitButton = () => {
    if (!attendance) {
      return false
    } else if (
      _.get(selectedCompany, '[0].lyra_code_required') === 'true' &&
      !hasRole(roles, ROLES.PAYMENTS_ADMIN) &&
      !guaranteedTime
    ) {
      return !eligibilityStatus || !formIsValid || isHPEligibilityLoading || pastPaymentLoading
    }
    return !formIsValid || isHPEligibilityLoading || pastPaymentLoading
  }

  const getProviderAddresses = () => {
    if ((hasRole(roles, ROLES.PAYMENTS_ADMIN) && isPractice) || hasRole(roles, ROLES.PRACTICES_ADMIN)) {
      return selectedProvider?.addresses
    } else {
      return providerAddresses
    }
  }

  if (!user.is_registered) {
    if (hasRole(roles, ROLES.PRACTICES_ADMIN)) {
      return <Navigate to={PRACTICES_REGISTER.route} />
    } else if (hasRole(roles, ROLES.INDIVIDUAL_PROVIDER)) {
      return <Navigate to={PROVIDERS_REGISTER.route} />
    }
  }

  if (loading) {
    return (
      <ContentLayout>
        <div className={styles['loading-container']}>
          <LoadingIndicator size={45} />
        </div>
      </ContentLayout>
    )
  }

  if (!loading && (hasRole(roles, ROLES.PRACTICES_ADMIN) || isPractice) && providersList?.length === 0) {
    return (
      <ContentLayout>
        <div className={styles['no-providers-container']}>
          <h1>No Providers Found</h1>
          <p className={styles['paragraph-small']}>
            No providers were found for this practice. Please align registered providers to this practice and try again.
          </p>
        </div>
      </ContentLayout>
    )
  }
  const { showModal, modalBody, modalType } = buildModals()
  const { initialValues, disabledFields: baseDisabledFields } = getInitialValues(guaranteedTime)
  const disabledFields = isLeidosUser(customerName)
    ? { ...baseDisabledFields, [PAYMENT_FORM.FIELDS.FIRST_NAME]: true, [PAYMENT_FORM.FIELDS.LAST_NAME]: true }
    : baseDisabledFields
  const showAvailabilityCard = !hasRole(roles, ROLES.PAYMENTS_ADMIN)
  return (
    <BootstrapContainer col='col-md-10 col-md-offset-1'>
      {showAvailabilityCard && <AvailabilityCard />}
      <div className={styles['title-block']}>
        <h1 className={styles.title}>Request Payment</h1>
      </div>
      <div className={styles['request-payment-container']}>
        <PaymentRequestForm
          attendance={attendance}
          dateInputKey={dateInputKey}
          disabledFields={disabledFields}
          handleSubmit={handleSubmit}
          submitFunction={_.throttle(submitForm, 2000)}
          submitting={submitting}
          providers={providersList}
          showEligibleMemberDetails={showEligibleMemberDetails}
          showOutcomesMessage={providerOutcomesAgreed}
          patientOutcomesAgreed={patientOutcomesAgreed}
          sessionType={sessionType}
          showDiagnosisDescription={showDiagnosisDescription}
          clientOutcomesEligibility={clientOutcomesEligibility}
          providerChangeHandler={providerChangeHandler}
          initialCompany={initialValues.company || initialCompany}
          initialDiagnoses={initialDiagnoses}
          lastSessionHandler={lastSessionHandler}
          sponsoringCompanies={displayCustomers ?? []}
          guaranteedTime={guaranteedTime}
          guaranteedTimeEligible={guaranteedTimeEligible}
          selectedCompany={selectedCompany}
          primaryButtonText={submitButtonText()}
          lyraCodeValidationUser={lyraCodeValidationInfo?.user_info}
          disableSubmitButton={shouldDisableSubmitButton()}
          showLyraCode={selectedCompany?.[0]?.lyra_code_enabled === 'true' && !guaranteedTime}
          hideDependentField={selectedCompany?.[0]?.hide_dependent_field === 'true'}
          selectedProvider={selectedProvider}
          loggedInUser={user}
          config={config}
          providerAddresses={getProviderAddresses() ?? []}
          locationChangeHandler={locationChangeHandler}
          primaryCondition={primaryCondition}
          secondaryCondition={secondaryCondition}
          ebtType={ebtType}
          rates={rates}
        />
        <BaseModal isOpen={showModal} body={modalBody} closeModal={() => closeRequestPaymentModal(modalType)} />
      </div>
    </BootstrapContainer>
  )
}

const selector = formValueSelector(PAYMENT_FORM.NAME)

const mapStateToProps = (state: RootState, ownProps: RequestPaymentProps) => {
  const userData = getAuthUser(state)
  // role based fields
  let practiceID
  let provider: ProviderUser | ProviderAdminProviderInfo | undefined = userData
  let rates = getRequestPaymentRates(state)
  const providersList = getRequestPaymentProvidersList(state) ?? []
  if (userData && 'roles' in userData && hasRole(userData?.roles, ROLES.PRACTICES_ADMIN)) {
    provider = getRequestPaymentSelectedProvider(state)
    practiceID = userData?.practice_id
  } else if (userData && 'roles' in userData && hasRole(userData?.roles, ROLES.PAYMENTS_ADMIN)) {
    provider = getProviderDetailsData(state)
    if (ownProps.isPractice) {
      provider = getRequestPaymentSelectedProvider(state)
      practiceID = getPracticeDetailsDataId(state)
    } else {
      rates = provider?.rates
    }
  }

  const attendance = selector(state, PAYMENT_FORM.FIELDS.ATTENDANCE)
  const relationship = selector(state, PAYMENT_FORM.FIELDS.RELATIONSHIP)
  const sessionType = selector(state, PAYMENT_FORM.FIELDS.SESSION_TYPE)
  const modality = selector(state, PAYMENT_FORM.FIELDS.MODALITY)
  const diagnosisArray = selector(state, PAYMENT_FORM.FIELDS.DIAGNOSIS)
  const firstName = selector(state, PAYMENT_FORM.FIELDS.FIRST_NAME)
  const lastName = selector(state, PAYMENT_FORM.FIELDS.LAST_NAME)
  const clientDOB = selector(state, PAYMENT_FORM.FIELDS.DATE_OF_BIRTH)
  let selectedCompany = selector(state, PAYMENT_FORM.FIELDS.COMPANY)
  selectedCompany = Iterable.isIterable(selectedCompany) ? selectedCompany.toJS() : selectedCompany
  const visitDate = selector(state, PAYMENT_FORM.FIELDS.VISIT_DATE)
  const lyraCode = selector(state, PAYMENT_FORM.FIELDS.LYRA_CODE)
  const eligibleMemberFirstName = selector(state, PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_FIRST_NAME)
  const eligibleMemberLastName = selector(state, PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_LAST_NAME)
  const eligibleMemberDOB = selector(state, PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_DOB)
  const guaranteedTime = selector(state, PAYMENT_FORM.FIELDS.IS_GUARANTEED_TIME)
  const customers = getRequestPaymentCustomers(state)
  const displayCustomers = getRequestPaymentFormSponsoringCompanies(state)
  const chargeCopy = getChargeCopyPrepopInfo(getRequestPaymentCopy(state), customers)
  const currentProviderLyraID = provider?.id || (provider as any)?.lyra_id
  const providerPaymentInfo = {
    provider_lyra_id: currentProviderLyraID,
    ...(practiceID && { practice_id: practiceID }),
  }
  const primaryCondition = selector(state, PAYMENT_FORM.FIELDS.PRIMARY_CONDITION)
  const secondaryCondition = selector(state, PAYMENT_FORM.FIELDS.SECONDARY_CONDITION)
  const ebtType = selector(state, PAYMENT_FORM.FIELDS.EBT_TYPE)

  // get patient outcomes and employer data
  const { patientOutcomesAgreed, patientEmployer } = getPatientPrepopInfo(getRequestPaymentPatient(state), customers)

  // Set outcomes eligibility text
  let isUnderage = false
  if (clientDOB !== undefined) {
    isUnderage = isValidDateOfBirth(clientDOB, { min: 0, max: 17 }).isValid
  }
  const isClientOutcomesEligible =
    !isUnderage &&
    (sessionType === undefined ||
      outcomesEligibleSessionTypes[sessionType as keyof typeof outcomesEligibleSessionTypes])
  const showEligibleMemberDetails = shouldShowPaymentFormEligibleMemberDetails(
    relationship,
    isUnderage,
    selectedCompany?.[0],
  )
  const eligibilityCheckFields = {
    [PAYMENT_FORM.FIELDS.COMPANY]: selectedCompany,
    [PAYMENT_FORM.FIELDS.FIRST_NAME]: firstName,
    [PAYMENT_FORM.FIELDS.LAST_NAME]: lastName,
    ...(_.get(selectedCompany, '[0].lyra_code_enabled') === 'true' && { [PAYMENT_FORM.FIELDS.LYRA_CODE]: lyraCode }),
    ...(showEligibleMemberDetails && {
      [PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_FIRST_NAME]: eligibleMemberFirstName,
      [PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_LAST_NAME]: eligibleMemberLastName,
      [PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_DOB]: eligibleMemberDOB,
    }),
    [PAYMENT_FORM.FIELDS.VISIT_DATE]: visitDate,
  }
  const HPEligibilityCheckFields = {
    ...eligibilityCheckFields,
    [PAYMENT_FORM.FIELDS.SESSION_TYPE]: sessionType,
    [PAYMENT_FORM.FIELDS.DATE_OF_BIRTH]: clientDOB,
    [PAYMENT_FORM.FIELDS.IS_GUARANTEED_TIME]: guaranteedTime,
    provider_lyra_id: currentProviderLyraID,
    ...(showEligibleMemberDetails && {
      [PAYMENT_FORM.FIELDS.RELATIONSHIP]: relationship,
    }),
  }
  const formState = (state.form as unknown as ImmutableTypedMap<typeof state.form>).toJS() as typeof state.form
  return {
    providersList,
    user: userData,
    guaranteedTime,
    guaranteedTimeEligible: provider?.guaranteed_time_eligible ?? false,
    showEligibleMemberDetails,
    sessionType,
    modality,
    rates,
    practiceID,
    prepopInfo: Object.assign({}, providerPaymentInfo, chargeCopy.prepopInfo),
    initialCompany: chargeCopy.initialCompany || patientEmployer,
    initialDiagnoses: chargeCopy.initialDiagnoses,
    showDiagnosisDescription: _.includes(diagnosisArray, 'Other'),
    clientOutcomesEligibility: isClientOutcomesEligible,
    providerOutcomesAgreed: provider?.outcomes_agreed ?? false,
    patientOutcomesAgreed,
    customers,
    displayCustomers,
    availableCodes: getHealthPlanICD10s(state),
    sessionTrackingStatus: getRequestPaymentDataSessionTrackingStatus(state),
    outcomeReminderStatus: getRequestPaymentDataOutcomeReminderStatus(state) ?? {},
    sessionsExceeded: getRequestPaymentResponseDataSessionTrackingStatus(state),
    showDuplicateModal: getRequestPaymentShowDuplicateModal(state),
    showCancelledSessionsExceededModal: getRequestPaymentShowCancelledSessionsExceededModal(state),
    paymentResponse: getRequestPaymentDataData(state),
    clientDOB,
    enteredVisitDate: visitDate,
    enteredPatient: {
      first_name: firstName,
      last_name: lastName,
      dob: clientDOB,
    },
    initialICD10s: Object.fromEntries(
      Object.entries(getRequestPaymentCopy(state) ?? {}).filter(
        ([key, value]: [string, string]) => [ICD10_SECONDARY_FIELD, ICD10_PRIMARY_FIELD].includes(key) && value,
      ),
    ),
    eligibilityCheckFields,
    HPEligibilityCheckFields,
    HPEligibility: getHealthPlanHPEligibility(state),
    currentHPCharge: getHealthPlanCurrentCharge(state),
    selectedCompany,
    selectedProvider: getRequestPaymentSelectedProvider(state),
    lyraCodeValidationInfo: getRequestPaymentLyraCodeValidationInfo(state) ?? {},
    synchronousErrors: getFormSyncErrors(PAYMENT_FORM.NAME)(state),
    showIneligibleClientModal: getRequestPaymentShowIneligibleClientModal(state),
    activeField: formState[PAYMENT_FORM.NAME]?.active,
    enteredlyraCode: lyraCode,
    eligibilityStatus: getRequestPaymentEligibilityStatus(state) ?? false,
    formMeta: getFormMeta(PAYMENT_FORM.NAME)(state),
    formIsValid: isValid(PAYMENT_FORM.NAME)(state),
    config: getAuthConfig(state),
    attendance,
    providerAddresses: getProviderDetailsAddresses(state),
    primaryCondition,
    secondaryCondition,
    ebtType,
    // When a payment admin submits on behalf of a practice, differential pricing is fetched
    // via getProviderRates and stored in requestPayment state. Otherwise, it's read from
    // the provider details state which is populated when viewing an individual provider.
    differentialPricingEnabled:
      hasRole(userData?.roles, ROLES.PAYMENTS_ADMIN) && !ownProps.isPractice
        ? getProviderDetailsDiffPricingEnabled(state)
        : getRequestPaymentDiffPricingEnabled(state),
    differentialPricingModifier:
      hasRole(userData?.roles, ROLES.PAYMENTS_ADMIN) && !ownProps.isPractice
        ? getProviderDetailsDiffPricingModifier(state)
        : getRequestPaymentDiffPricingModifier(state),
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    actions: bindActionCreators(
      {
        ...requestPaymentActions,
        ...alertActions,
        ...clientsDataActions,
        ...providerDetailsActions,
        saveHealthPlanCharge,
        getHPEligibility,
        clearHPEligibility,
        clearHealthPlanCharge,
        getPayments,
      },
      dispatch,
    ),
  }
}

export const RequestPaymentReduxForm = reduxForm({
  form: PAYMENT_FORM.NAME,
  validate: paymentRequestValidate,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  asyncValidate: paymentRequestAsyncValidate,
  warn: requestPaymentWarnings,
  // @ts-expect-error TS(2367): This condition will always return 'false' since th... Remove this comment to see the full error message
  shouldAsyncValidate: ({ trigger }) => trigger === 'manual',
})(RequestPaymentAdmins)

export default connect(mapStateToProps, mapDispatchToProps)(RequestPaymentReduxForm)
